import React, { useEffect, useState } from 'react';
import './HearsayForm.css';

const defaultFormState = {
    submitterName: '',
    submitterEmail: '',
    placeName: '',
    latitude: '',
    longitude: '',
    narrative: '',
    sourceReference: ''
};

const HearsayForm = ({ selectedLocation }) => {
    const [formState, setFormState] = useState(defaultFormState);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [fieldErrors, setFieldErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Keep the location inputs synced with any map clicks so visitors stay oriented.
    useEffect(() => {
        if (!selectedLocation) return;
        setFormState((prev) => ({
            ...prev,
            latitude: selectedLocation.latitude ?? '',
            longitude: selectedLocation.longitude ?? ''
        }));
    }, [selectedLocation]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormState((prev) => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormState({
            ...defaultFormState,
            latitude: selectedLocation?.latitude ?? '',
            longitude: selectedLocation?.longitude ?? ''
        });
        setFieldErrors({});
        setStatus({ type: 'success', message: 'Thanks for sharing. Our researchers will review your note shortly.' });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setStatus({ type: '', message: '' });
        setFieldErrors({});

        const latitudeValue = formState.latitude === '' ? null : Number(formState.latitude);
        const longitudeValue = formState.longitude === '' ? null : Number(formState.longitude);
        const payload = {
            submitter_name: formState.submitterName.trim(),
            submitter_email: formState.submitterEmail.trim(),
            place_name: formState.placeName.trim(),
            latitude: Number.isFinite(latitudeValue) ? latitudeValue : null,
            longitude: Number.isFinite(longitudeValue) ? longitudeValue : null,
            narrative: formState.narrative.trim(),
            source_reference: formState.sourceReference.trim()
        };

        try {
            const response = await fetch('/api/hearsay/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const body = await response.json().catch(() => ({}));

            if (!response.ok) {
                setStatus({ type: 'error', message: body.detail || 'Please double-check the highlighted fields.' });
                setFieldErrors(body.errors || {});
            } else {
                resetForm();
            }
        } catch (error) {
            setStatus({ type: 'error', message: 'Submission failed due to connectivity. Please try again.' });
            console.error('Error submitting hearsay data:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="hearsay-form-wrapper" aria-live="polite">
            <div className="hearsay-form-header">
                <h2>Share a hearsay data point</h2>
                <p>
                    Click anywhere on the map to preload coordinates, then outline the account so our researchers can validate
                    and publish it appropriately.
                </p>
            </div>

            {status.message && (
                <div className={`form-status ${status.type === 'error' ? 'error' : 'success'}`}>
                    {status.message}
                </div>
            )}

            <form className="hearsay-form" onSubmit={handleSubmit}>
                <section className="privacy-notice" aria-labelledby="privacy-notice-heading">
                    <h3 id="privacy-notice-heading">Privacy and collection notice</h3>
                    <p>
                        Griffith University collects your name, optional email address and the account, location and source
                        details you provide to review and verify your contribution to the Names of Places project.
                        Personal information is collected, used and disclosed in accordance with the{' '}
                        <a
                            href="https://www.griffith.edu.au/about-griffith/corporate-governance/plans-publications/griffith-university-privacy-statement"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Griffith University Privacy Statement (opens in a new tab)
                        </a>
                        . The statement explains how to request access to or correction of your personal information,
                        and how to make a privacy enquiry or complaint.
                    </p>
                    <p>
                        Your contributor name and contact email are kept for authorised staff review and are not included
                        in public attribution. Approved accounts, locations and source references may appear on the public
                        map. Do not include contact details or other private personal information in your account or source
                        references, and only share information you are authorised to provide.
                    </p>
                </section>
                <div className="form-row">
                    <label htmlFor="submitterName">Your name *</label>
                    <input
                        id="submitterName"
                        name="submitterName"
                        type="text"
                        value={formState.submitterName}
                        onChange={handleChange}
                        required
                        aria-invalid={!!fieldErrors.submitter_name}
                        aria-describedby={fieldErrors.submitter_name ? 'submitterName-error' : undefined}
                    />
                    {fieldErrors.submitter_name && (
                        <p id="submitterName-error" className="field-error">{fieldErrors.submitter_name}</p>
                    )}
                </div>

                <div className="form-row">
                    <label htmlFor="submitterEmail">Contact email (optional)</label>
                    <input
                        id="submitterEmail"
                        name="submitterEmail"
                        type="email"
                        value={formState.submitterEmail}
                        onChange={handleChange}
                    />
                </div>

                <div className="form-row">
                    <label htmlFor="placeName">Place name *</label>
                    <input
                        id="placeName"
                        name="placeName"
                        type="text"
                        value={formState.placeName}
                        onChange={handleChange}
                        required
                        aria-invalid={!!fieldErrors.place_name}
                        aria-describedby={fieldErrors.place_name ? 'placeName-error' : undefined}
                    />
                    {fieldErrors.place_name && (
                        <p id="placeName-error" className="field-error">{fieldErrors.place_name}</p>
                    )}
                </div>

                <div className="form-row coordinate-row">
                    <div>
                        <label htmlFor="latitude">Latitude</label>
                        <input
                            id="latitude"
                            name="latitude"
                            type="number"
                            step="0.000001"
                            value={formState.latitude}
                            onChange={handleChange}
                            aria-invalid={!!fieldErrors.latitude}
                            aria-describedby={fieldErrors.latitude ? 'latitude-error' : undefined}
                        />
                        {fieldErrors.latitude && (
                            <p id="latitude-error" className="field-error">{fieldErrors.latitude}</p>
                        )}
                    </div>
                    <div>
                        <label htmlFor="longitude">Longitude</label>
                        <input
                            id="longitude"
                            name="longitude"
                            type="number"
                            step="0.000001"
                            value={formState.longitude}
                            onChange={handleChange}
                            aria-invalid={!!fieldErrors.longitude}
                            aria-describedby={fieldErrors.longitude ? 'longitude-error' : undefined}
                        />
                        {fieldErrors.longitude && (
                            <p id="longitude-error" className="field-error">{fieldErrors.longitude}</p>
                        )}
                    </div>
                </div>

                <div className="form-row">
                    <label htmlFor="narrative">Narrative / hearsay account *</label>
                    <textarea
                        id="narrative"
                        name="narrative"
                        rows="5"
                        value={formState.narrative}
                        onChange={handleChange}
                        required
                        aria-invalid={!!fieldErrors.narrative}
                        aria-describedby={fieldErrors.narrative ? 'narrative-error' : undefined}
                    />
                    {fieldErrors.narrative && (
                        <p id="narrative-error" className="field-error">{fieldErrors.narrative}</p>
                    )}
                </div>

                <div className="form-row">
                    <label htmlFor="sourceReference">Who shared this account?</label>
                    <input
                        id="sourceReference"
                        name="sourceReference"
                        type="text"
                        value={formState.sourceReference}
                        onChange={handleChange}
                    />
                </div>

                <div className="cta-row">
                    <button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Sending...' : 'Send for researcher review'}
                    </button>
                    <p className="disclaimer">
                        * Required. Submissions are held for verification and will not publish automatically.
                    </p>
                </div>
            </form>
        </section>
    );
};

export default HearsayForm;
